import { useMemo } from 'react'

export type Heading = {
  id: string
  text: string
  level: number
  children?: Heading[]
}

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()

export const useHeadings = (html: string | undefined) => {
  return useMemo(() => {
    if (!html) return { headings: [] as Heading[], html: '' }

    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const elements = doc.querySelectorAll('h1, h2, h3, h4')

    const flat: Heading[] = []
    const usedIds = new Map<string, number>()

    elements.forEach((el) => {
      const text = el.textContent ?? ''
      let id = slugify(text) || `heading-${flat.length}`

      const count = usedIds.get(id) ?? 0

      usedIds.set(id, count + 1)
      if (count > 0) id = `${id}-${count}`

      el.setAttribute('id', id)
      flat.push({ id, text, level: Number.parseInt(el.tagName[1]) })
    })

    const headings: Heading[] = []
    const stack: Heading[] = []

    for (const heading of flat) {
      while (
        stack.length > 0 &&
        stack[stack.length - 1].level >= heading.level
      ) {
        stack.pop()
      }

      if (stack.length === 0) {
        headings.push(heading)
      } else {
        const parent = stack[stack.length - 1]

        parent.children = parent.children ?? []
        parent.children.push(heading)
      }

      stack.push(heading)
    }

    let firstImageP = true

    doc.body.querySelectorAll('p').forEach((p) => {
      if (!p.querySelector('img')) return

      if (firstImageP) {
        // Promote first image paragraph to h2 (acts as hero/banner image)
        const h2 = doc.createElement('h2')

        Array.from(p.attributes).forEach((attr) =>
          h2.setAttribute(attr.name, attr.value)
        )
        h2.style.textAlign = 'center'
        h2.innerHTML = p.innerHTML
        p.replaceWith(h2)
        firstImageP = false
      } else {
        p.style.textAlign = 'center'
      }
    })

    return { headings, html: doc.body.innerHTML }
  }, [html])
}
